import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Audience, InsightDocument } from "../api/insights";
import type { BacklogSummary } from "../api/remediation";
import { riskLevelColor } from "../lib/riskVocabulary";
import { ConfidenceChip, FindingRow, Kpi, NAVY, Panel, costRange, shortDate } from "./cockpit/primitives";
import { Heatmap, RegulatoryClock, statusTag } from "./cockpit/charts";
import { PlanningYearTables, planningBasisText, CategoryHeat, ModuleDistributionTable, NextStepsPanel, PriorityBar, ReadinessPanel, TimeChart, ViewsScatter } from "./cockpit/views";
import { MandatesPanel } from "./cockpit/mandates";
import { ReportsMenu } from "./cockpit/reports-menu";
import { jurisdictionLabel } from "../lib/mandateLabels";
import { CompliancePanel, CoveragePanel, CryptoPosture, DecisionsPanel, DependenciesPanel, RiskRegister, VendorTable, WavePlan } from "./cockpit/panels";

/**
 * Executive cockpit — the Overview tab (Sprints 3–5).
 *
 * Built from the run's insight document, the same document the three reports render. Three lenses:
 *   Executive — the answer (headline), five KPIs, the top risks (without repeating the headline),
 *               the decisions asked and organisational readiness (C-033).
 *   Detailed Risk Assessment (id `ciso`) — a superset: every finding, two views, risk by category and
 *               module, organisational readiness, the register with asset cards, the time chart, mandates
 *               and regulatory clock, cryptography, compliance, vendors, trust.
 *   Programme — next steps and measures, waves with backlog progress, decisions, readiness, deadlines.
 * Waves, budget and the backlog are migration planning: they appear in the Migration programme lens only
 * (Step 2, C-018).
 * KPIs: Priority 1, out of time (against the planning year), High+, fix now (broken today), tested deadline.
 * Findings are one line (title + action); detail and evidence expand on click.
 */

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

type Lens = "executive" | "ciso" | "programme";
const LENSES: Array<{ id: Lens; label: string; audience: Audience }> = [
  { id: "executive", label: "Executive", audience: "board" },
  { id: "ciso", label: "Detailed Risk Assessment", audience: "ciso" },
  { id: "programme", label: "Migration programme", audience: "programme" },
];

const LENS_KEY = "qram.cockpit.lens";
function initialLens(): Lens {
  try {
    const v = window.localStorage.getItem(LENS_KEY);
    if (v === "executive" || v === "ciso" || v === "programme") return v;
  } catch { /* storage unavailable */ }
  return "executive";
}

export function ExecutiveCockpit({ projectId, onOpenBacklog }: { projectId: string; onOpenBacklog?: () => void }) {
  const [lens, setLensState] = useState<Lens>(initialLens);
  const setLens = (l: Lens) => {
    setLensState(l);
    try { window.localStorage.setItem(LENS_KEY, l); } catch { /* ignore */ }
  };
  const q = useQuery({
    queryKey: ["project-insights", projectId],
    queryFn: () => api.get<InsightDocument>(`/projects/${projectId}/insights/latest`),
  });
  const backlog = useQuery({
    queryKey: ["remediation-summary", projectId],
    queryFn: () => api.get<BacklogSummary>(`/projects/${projectId}/remediation/summary`),
    enabled: !!q.data,
  });

  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of q.data?.timeline ?? []) m.set(t.asset_id, t.asset_name);
    return m;
  }, [q.data]);

  if (q.isLoading) return <div className="p-8 text-ink-500 text-sm italic">Building executive view…</div>;
  if (q.isError || !q.data) return null; // the statistics view below shows the empty state

  const d = q.data;
  const k = d.kpis;
  const tw = d.time_window;
  const deadline = d.mandates.deadline;
  /** The run's one planning year (shown on the clock, in the header and in every time panel). */
  const quantumYear = d.planning_year.year;
  const audience = LENSES.find((l) => l.id === lens)!.audience;
  // The Detailed Risk Assessment lens is a superset: it lists every finding (C-014). The Executive lens
  // leaves out the finding the headline already states (C-033).
  // Findings that are migration planning (quick wins, interim controls) belong to the Migration programme lens (N-10).
  const PLANNING_FINDINGS = new Set(["RDY-QUICKWIN", "RDY-STRATEGIC"]);
  const lensFindings = lens === "ciso"
    ? d.insights.filter((i) => !PLANNING_FINDINGS.has(i.id))
    : d.insights.filter((i) => i.audiences.includes(audience) && !(lens === "executive" && i.title === d.headline));
  const currency = k.cost?.currency ?? null;
  const highDelta = d.trend && d.trend.comparable ? d.trend.overall_high_plus_now - d.trend.overall_high_plus_previous : null;
  const regMilestones = d.regulatory.milestones;

  const findings = (list = lensFindings, max?: number) => (
    <ul className="divide-y divide-ink-50" data-testid="findings">
      {(max ? list.slice(0, max) : list).map((i) => <FindingRow key={i.id} insight={i} names={names} />)}
      {max && list.length > max && <li className="pt-2 text-[11px] text-ink-500">+{list.length - max} more in the Detailed Risk Assessment lens</li>}
    </ul>
  );

  const clock = (
    <Panel
      title="Regulatory clock"
      subtitle={`Dated milestones that concern ${d.mandates.organisation.jurisdictions.map(jurisdictionLabel).join(", ") || "this organisation"} · numbers = assets projected to miss on the current start dates`}
      testId="panel-regulatory"
    >
      <RegulatoryClock milestones={regMilestones} crqcYear={quantumYear} asOf={d.run_completed_at} />
    </Panel>
  );
  /** Assets already quantum-safe are in no wave and outside the out-of-time test (C-011). */
  const migratedN = k.late_migrations.migrated ?? 0;
  const alreadySafe = migratedN > 0 ? ` · ${migratedN} already quantum-safe ${migratedN === 1 ? "asset is" : "assets are"} in no wave` : "";
  const waves = (
    <Panel
      title="Migration waves"
      subtitle={`${k.cost ? `Estimated ${costRange(k.cost.low, k.cost.open_ended > 0 ? null : k.cost.high, currency)} for the ${k.cost.assets_costed} of ${k.cost.assets_total} assets still to migrate that carry an estimate` : "Costs appear when assets carry a migration cost estimate"}${alreadySafe}`}
      action={onOpenBacklog && <button type="button" onClick={onOpenBacklog} className="text-xs text-accent hover:underline whitespace-nowrap">Open backlog →</button>}
      testId="panel-waves"
    >
      <WavePlan waves={d.waves} backlog={backlog.data} currency={currency} />
    </Panel>
  );
  const vendors = (limit?: number) => (
    <Panel title="Vendor & supply chain" subtitle="Least-ready rating across each vendor's assets" testId="panel-vendors">
      <VendorTable vendors={d.vendors} limit={limit} />
    </Panel>
  );
  const coverage = (
    <Panel title="How far to trust this" subtitle={k.confidence.basis} testId="panel-coverage">
      <CoveragePanel doc={d} />
    </Panel>
  );
  // Funding a migration wave (and its budget) is migration planning: only the Migration programme lens asks it (C-018).
  const PLANNING_DECISIONS = new Set(["DEC-WAVE1"]);
  const decisions = (
    <Panel title="Decisions requested" subtitle="Ordered by score (severity × assets affected × urgency); the reports' board decisions are the top four for the Board and Procurement · hover an item for the reason" testId="panel-decisions">
      <DecisionsPanel decisions={lens === "programme" ? d.decisions : d.decisions.filter((x) => !PLANNING_DECISIONS.has(x.id))} />
    </Panel>
  );
  const readinessPanel = (
    <Panel title="Organisational readiness" subtitle="Gap assessment, verified · average rating per category, 1–4" testId="panel-readiness">
      <ReadinessPanel readiness={d.readiness} />
    </Panel>
  );
  const register = (limit = 10) => (
    <Panel title="Asset risk register" subtitle="Ranked by priority, then both views combined · click a row for how the score was built and the evidence" testId="panel-register">
      <RiskRegister rows={d.risk_register} currency={currency} cards={d.cards} cardFor={d.card_for} limit={limit} planning={lens === "programme"} />
    </Panel>
  );
  const lm = k.late_migrations;
  // One source of facts (Step 7c): the same keys the reports print.
  const xz = d.facts?.values["time.x_exceeds_z"] ?? lm.overdue;
  const deadlineMiss = k.next_deadline?.projected_to_miss ?? 0;
  const appetiteText = `the planning year ${d.planning_year.year} (${planningBasisText(d.planning_year)})`;
  const c = lm.by_cause;
  const causes = [
    c.finish ? `${c.finish} finish${c.finish === 1 ? "es" : ""} migrating after the planning year` : "",
    c.lifetime ? `${c.lifetime} finish${c.lifetime === 1 ? "es" : ""} migrating in time, but the data or signatures ${c.lifetime === 1 ? "it protects" : "they protect"} must stay secure beyond it` : "",
    c.vendor ? `${c.vendor} wait${c.vendor === 1 ? "s" : ""} for a vendor with no PQC date` : "",
  ].filter(Boolean).join("; ");
  const deadlineWord = deadline ? (deadline.applicability === "obligation" ? `deadline set by ${deadline.set_by}` : `date recommended by ${deadline.set_by} (guidance)`) : "";
  const whyNow = lm.count > 0
    ? `${lm.count} asset${lm.count === 1 ? " is" : "s are"} out of time against ${appetiteText}: ${causes}.${deadline && deadlineMiss > 0 ? ` ${deadlineMiss} ${deadlineMiss === 1 ? "is" : "are"} projected to miss the ${deadline.date.slice(0, 4)} ${deadlineWord}.` : ""}`
    : deadline && deadlineMiss > 0
      ? `No asset is out of time against the planning year, but ${deadlineMiss} ${deadlineMiss === 1 ? "is" : "are"} projected to miss the ${deadline.date.slice(0, 4)} ${deadlineWord} on current start dates.`
      : `No asset is out of time against ${appetiteText} on current estimates; ${lm.tight} ha${lm.tight === 1 ? "s" : "ve"} two years or less to spare.`;
  const timePanel = (limit = 12) => (
    <Panel title="How much time is left" subtitle={`Start delay (D) + migration time (Y), and how long the data must stay protected (X), against Z, the years left to the CRQC planning year ${d.planning_year.year}. Out of time when D + Y > Z or X + D + Y > Z`} testId="panel-time">
      <div className="grid grid-cols-5 gap-5">
        <div className="col-span-3">
          <TimeChart tw={tw} deadlineYears={deadline?.years ?? null} limit={limit} />
          <p className="text-[11px] text-ink-500 mt-2" data-testid="time-inputs">
            Inputs: data lifetime recorded for {tw.inputs.s_captured} of {tw.inputs.assets} (shelf life used for {tw.inputs.s_shelf_life}); migration time captured for {tw.inputs.m_captured} ({tw.inputs.m_default} use the 5-year default); start planned for {tw.inputs.start_asset + tw.inputs.start_programme}, under way for {tw.inputs.in_progress}, not planned for {tw.inputs.start_not_planned} (assumed to start today). Set the planning year and start dates in Settings → Reporting profile.
          </p>
        </div>
        <div className="col-span-2"><PlanningYearTables doc={d} /></div>
      </div>
    </Panel>
  );
  const mandates = (limit = 8) => (
    <Panel title="Regulatory mandates" subtitle="Instruments that concern this organisation, and the one that sets the deadline test" testId="panel-mandates">
      <MandatesPanel mandates={d.mandates} limit={limit} />
    </Panel>
  );

  return (
    <div className="space-y-5" data-testid="executive-cockpit">
      {/* Masthead */}
      <header className="flex items-start justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-ink-500">Quantum risk position · {d.project.name}</p>
          <p className="text-xs text-ink-500 mt-1 flex items-center gap-1.5 flex-wrap">
            <span>Assessed {d.run_completed_at ? new Date(d.run_completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
            <span data-testid="header-planning-year" title="The year this assessment assumes a cryptographically relevant quantum computer (CRQC) could exist">· CRQC planning year <strong className="text-ink-700">{d.planning_year.year}</strong> ({planningBasisText(d.planning_year)})</span>
            {d.planning_year.legacy_scores && <span data-testid="legacy-scores">· dimension scores as scored on {d.planning_year.legacy_scores.scored_on ?? "the run date"}{d.planning_year.legacy_scores.timeline_year !== null && d.planning_year.legacy_scores.timeline_year !== d.planning_year.year ? ` (Timeline used ${d.planning_year.legacy_scores.timeline_year})` : ""}</span>}
            <span>·</span>
            <ConfidenceChip level={k.confidence.level} basis={k.confidence.basis} />
          </p>
        </div>
        <ReportsMenu projectId={projectId} primary={lens === "executive" ? "risk-assessment-simplified" : lens === "ciso" ? "risk-assessment-detailed" : "programme"} />
      </header>

      {/* Lens switch */}
      <nav className="flex gap-1 border-b border-ink-100" aria-label="View for">
        {LENSES.map((l) => (
          <button key={l.id} type="button" onClick={() => setLens(l.id)} data-testid={`lens-${l.id}`}
            className={`px-3 py-1.5 text-xs font-medium -mb-px border-b-2 ${lens === l.id ? "border-accent text-ink-900" : "border-transparent text-ink-500 hover:text-ink-800"}`}>
            {l.label}
          </button>
        ))}
      </nav>

      {/* Headline */}
      {d.headline && lens === "executive" && (
        <p className="font-display text-xl leading-snug text-ink-900 border-l-4 pl-4" style={{ borderColor: NAVY }} data-testid="cockpit-headline">{d.headline}</p>
      )}

      {/* KPIs */}
      <section className="grid grid-cols-5 gap-3" aria-label="Key indicators">
        <Kpi testId="kpi-p1" label="Priority 1 · act now" value={String(k.priorities.P1)} unit="assets"
          sub={`P2 ${k.priorities.P2} · P3 ${k.priorities.P3} · P4 ${k.priorities.P4}`}
          title="High or above, and out of time or missing the deadline"
          accent={k.priorities.P1 > 0 ? riskLevelColor("Critical") : NAVY} />
        <Kpi testId="kpi-late" label="Out of time" value={String(lm.count)} unit={`of ${lm.of}`}
          sub={`${xz > 0 ? `${xz} too late even if migrated today (X > Z)` : lm.tight > 0 ? `+${lm.tight} with ≤ 2 years to spare` : "start + migration vs planning year"}${(lm.migrated ?? 0) > 0 ? ` · ${lm.migrated} already quantum-safe` : ""}${(lm.untested ?? 0) > 0 ? ` · ${lm.untested} not testable` : ""}`}
          accent={lm.count > 0 ? riskLevelColor("Very_high") : undefined} title="Cannot finish migrating (or protecting the data) before the planning year" />
        <Kpi testId="kpi-high" label="High-risk assets" value={String(k.overall_high_plus.count)} unit={`of ${k.overall_high_plus.of}`}
          sub={highDelta === null ? "Overall risk High or above" : `${highDelta > 0 ? "▲" : highDelta < 0 ? "▼" : "■"} ${Math.abs(highDelta)} vs ${shortDate(d.trend!.baseline_completed_at)}`}
          accent={k.overall_high_plus.count > 0 ? riskLevelColor("High") : undefined} />
        <Kpi testId="kpi-fixnow" label="Fix now" value={String(k.fix_now.assets)} unit="assets"
          sub={k.fix_now.assets > 0 ? `${k.fix_now.external} reachable from outside · broken today` : "no cryptography broken today"}
          title="Uses cryptography that is broken today, not only by a CRQC — a separate track from the quantum programme"
          accent={k.fix_now.assets > 0 ? riskLevelColor("Critical") : undefined} />
        <Kpi testId="kpi-deadline" label="Deadline tested" value={k.next_deadline ? shortDate(k.next_deadline.date) : "None"}
          sub={k.next_deadline ? `${cap(k.next_deadline.set_by)} · ${statusTag(k.next_deadline.applicability, k.next_deadline.binding)}${k.next_deadline.projected_to_miss > 0 ? ` · ${k.next_deadline.projected_to_miss} to miss` : ""}` : "no dated completion deadline applies"}
          note={deadline ? `Track applied: ${deadline.track ?? (deadline.short_title ?? deadline.instrument)}` : undefined}
          title={deadline ? deadline.basis : k.next_deadline?.framework}
          accent={k.next_deadline && k.next_deadline.projected_to_miss > 0 ? riskLevelColor("Critical") : undefined} />
      </section>

      {lens === "executive" && (
        <>
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-3"><Panel title="What matters most" subtitle="Click a finding for detail and evidence" testId="panel-findings">{findings(lensFindings, 6)}</Panel></div>
            <div className="col-span-2 space-y-4">
              <Panel title="Assets by priority" testId="panel-priority">
                <PriorityBar priorities={k.priorities} />
                <div className="mt-3 border-l-4 pl-3 py-1 text-xs text-ink-700" style={{ borderColor: riskLevelColor("Critical") }} data-testid="why-now"><strong className="block text-ink-900 mb-0.5">Why this cannot wait</strong>{whyNow}</div>
              </Panel>
              {decisions}
              {readinessPanel}
            </div>
          </div>
        </>
      )}

      {lens === "ciso" && (
        <>
          <Panel title="Findings" subtitle={`${lensFindings.length} ${lensFindings.length === 1 ? "finding" : "findings"} · every risk finding of this run (migration-planning findings are in the Migration programme lens) · click for detail and evidence`} testId="panel-findings">{findings()}</Panel>
          <div className="grid grid-cols-2 gap-4">
            <Panel title="Risk at a glance" subtitle={`Baseline risk (business exposure) against Technical exposure: ${d.views.overlap.both} High+ on both · ${d.views.overlap.exposure_only} only on Technical exposure · ${d.views.overlap.baseline_only} only on Baseline risk`}><ViewsScatter points={d.views.points} /></Panel>
            <Panel title="How the assets score on each module" subtitle="Assets by level, mean score and High+ count"><ModuleDistributionTable rows={d.module_distribution} /></Panel>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><Panel title="Risk by category" subtitle="Worst asset per module in each asset category · assets by priority" testId="panel-category"><CategoryHeat categories={d.categories} /></Panel></div>
            {readinessPanel}
          </div>
          {register(10)}
          {timePanel(15)}
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-2">{mandates(12)}</div>
            <div className="col-span-3">{clock}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Panel title="Cryptographic posture" testId="panel-crypto"><CryptoPosture crypto={d.crypto} /></Panel>
            <Panel title="Policy compliance" testId="panel-compliance"><CompliancePanel doc={d} /></Panel>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><Panel title="Risk by business unit" subtitle="Band of the mean score · High+ count"><Heatmap doc={d} /></Panel></div>
            <Panel title="Shared dependencies" subtitle="Fix once, de-risk many"><DependenciesPanel deps={d.dependencies} /></Panel>
          </div>
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-3">{vendors(d.vendors.rows.length)}</div>
            <div className="col-span-2">{coverage}</div>
          </div>
        </>
      )}

      {lens === "programme" && (
        <>
          <Panel title="Next steps" subtitle="Derived from this run's findings · track the measures each quarter" testId="panel-next"><NextStepsPanel next={d.next_steps} /></Panel>
          {waves}
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-3"><Panel title="Programme findings" testId="panel-findings">{findings()}</Panel></div>
            <div className="col-span-2 space-y-4">{decisions}{readinessPanel}</div>
          </div>
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-2">{mandates(10)}</div>
            <div className="col-span-3">{clock}</div>
          </div>
          {register(15)}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">{coverage}</div>
            <Panel title="Shared dependencies" subtitle="Fix once, de-risk many"><DependenciesPanel deps={d.dependencies} /></Panel>
          </div>
          {vendors()}
        </>
      )}
    </div>
  );
}
